import React, { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import getConfig from "next/config";
import { isMobile } from "react-device-detect";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";

import { hexToRGBA } from "../../../utils/theme";

const { publicRuntimeConfig } = getConfig();

const StyledGroup = styled(Form.Group)`
  position: relative;

  padding-left: 0;
  padding-right: 0;

  ${({ withLabel }) => withLabel && css`
    padding-top: 24px;
  `};
`;

const StyledLabel = styled(Form.Label)`
  position: absolute;
  left: 0;
  top: 0;
  font-family: 'LatoBold';
  font-size: 12px;
  line-height: 24px;
  text-transform: uppercase;
  white-space: nowrap;
  color: #078FF1;

  span {
    font-size: 16px;
    color: #C61F0C;
  }
`;

const StyledControl = styled(Form.Control)`
  width: 100%;
  min-height: 52px;

  padding-top: 14px;
  padding-bottom: 8px;
  padding-left: 100px;
  padding-right: 16px;

  font-family: 'LatoRegular';
  font-size: 14px;
  line-height: 24px;
  color: ${hexToRGBA('#1B3858', 0.5)};

  border: 1px solid ${hexToRGBA('#078FF1', 0.1)};
  border-radius: 7px;

  background-color: ${hexToRGBA('#078FF1', 0.1)};

  ${({ isValid }) => isValid && css`
    border: 1px solid #198754 !important;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_success.svg)`} !important;
    background-repeat: no-repeat;
    background-position: right calc(0.375em + 0.1875rem) center;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
    box-shadow: 0 0 0 0.2rem rgb(40 167 69 / 25%) !important;
  `};

  ${({ isInvalid }) => isInvalid && css`
    border: 1px solid #DC3545 !important;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_failure.svg)`} !important;
    background-repeat: no-repeat;
    background-position: right calc(0.375em + 0.1875rem) center;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
    box-shadow: 0 0 0 0.2rem rgb(220 53 69 / 25%) !important;
  `};

  &::placeholder {
    color: ${hexToRGBA('#1B3858', 0.5)};
  }

  &::-webkit-file-upload-button {
    position: absolute;
    top: 24px;
    left: 0;
    margin: 0;
    min-height: 52px;
    border: 1px solid ${hexToRGBA('#078FF1', 0.1)};
    border-top-left-radius: 7px;
    border-bottom-left-radius: 7px;
    background-color: #078FF1;
    color: #FFFFFF;
    cursor: pointer;

    ${({ labeled }) => !labeled && css`
      top: 0;
    `};
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)``;

function Upload({
  id,
  hint,
  label,
  emptyLabel,
  accept,
  required,
  defaultInvalidMessage = '',
  invalid,
  validate,
  validated,
  value,
  onChange,
}) {
  const handleChange = useCallback((event) => {
    validate && validate(event, { required });
    onChange && onChange(event);
  }, [onChange, validate, required]);

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? "top" : "right-start",
      delay: { show: 0, hide: 0 },
      trigger: ["hover", "focus"],
      overlay: (
        <Tooltip id={`tooltip-${id}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [id, hint]);

  const isValid = !required && validated && !invalid || value && validated && !invalid;
  const isInvalid = required && !value && validated || invalid && validated;
  const showMessage = !(isValid && !isInvalid);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup
        controlId={id}
        className={invalid ? 'custom-control-invalid' : ''}
        withLabel={Boolean(label || emptyLabel)}
      >
        <StyledLabel>
          {label}
          {!emptyLabel && label && required && (<span>&nbsp;*</span>)}
        </StyledLabel>
        <StyledControl
          type="file"
          name={id}
          required={required}
          labeled={Boolean(label)}
          accept={accept}
          isValid={isValid}
          isInvalid={isInvalid}
          onChange={handleChange}
        />
        {showMessage && (
          <StyledFeedback type="invalid">
            {defaultInvalidMessage}
          </StyledFeedback>
        )}
      </StyledGroup>
    </OverlayTrigger>
  );
}

export default Upload;
